import { DestroyRef, Directive, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { asyncScheduler, defer, fromEvent, merge, Observable, repeat } from 'rxjs';
import { debounceTime, delay, map, share, switchMap, takeUntil, tap } from 'rxjs/operators';

import { ConnectedOverlayDirective } from '../../../core/overlay/overlay-directives';

@Directive({
  standalone: true,
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[rt-overlay-hover-trigger]',
})
export class OverlayHoverTriggerDirective {
  public destroyRef = inject(DestroyRef);

  @Input() public hoverHideDebounce: number = 500;
  @Input() public hoverShowDebounce: number = 200;
  @Input('rt-overlay-hover-trigger') public connectedOverlay: ConnectedOverlayDirective;

  public get triggerEl() {
    return this.connectedOverlay.origin.elementRef.nativeElement;
  }

  public get paneElRef() {
    return this.connectedOverlay.overlayRef.overlayElement;
  }

  public ngAfterViewInit() {
    const triggerShow$ = this.getMappedEventStream(this.triggerEl, 'mouseenter', true);
    const triggerHide$ = this.getMappedEventStream(this.triggerEl, 'mouseleave', false);

    const paneCache: { show$?: Observable<true>; hide$?: Observable<false> } = {
      show$: undefined,
      hide$: undefined,
    };

    const paneShow$ = defer(() => {
      paneCache.show$ =
        paneCache.show$ ?? this.getMappedEventStream(this.paneElRef, 'mouseenter', true);
      return paneCache.show$!;
    });

    const paneHide$ = defer(() => {
      paneCache.hide$ =
        paneCache.hide$ ?? this.getMappedEventStream(this.paneElRef, 'mouseleave', false);
      return paneCache.hide$!;
    });

    triggerShow$
      .pipe(
        debounceTime(this.hoverShowDebounce),
        takeUntil(triggerHide$),
        repeat(),
        tap(() => this.show()),
        delay(1, asyncScheduler),
        switchMap(() =>
          merge(triggerHide$, paneHide$).pipe(
            debounceTime(this.hoverHideDebounce),
            takeUntil(merge(triggerShow$, paneShow$)),
            tap(() => this.hide()),
            repeat(),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private getMappedEventStream<T extends boolean>(
    el: HTMLElement,
    name: string,
    value: T,
  ): Observable<T> {
    return fromEvent(el, name).pipe(
      map(() => value),
      share(),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  private show() {
    this.connectedOverlay.open = true;
  }

  private hide() {
    this.connectedOverlay.open = false;
  }
}
